/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 图片下载处理 Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-01-09 17:26:51
 */

import { useCallback } from 'react'
import { message } from 'antd'
import { useAppDispatch, useGroupID } from '../../../../app/hooks'
import { removeWatermarkDownload } from '../../../../actions/photo'

// 下载进度回调函数类型
export type DownloadProgressCallback = (current: number, total: number) => void

// 定义下载 Hook 返回类型
export interface UseImageDownloadReturn {
  // 下载单张图片
  downloadImage: (
    url: string,
    fileName?: string,
    originalUrl?: string,
    onProgress?: DownloadProgressCallback
  ) => Promise<void>
  // 批量下载图片（支持原始URL数组用于提取文件名）
  downloadImages: (
    urls: string[],
    originalUrls?: string[],
    baseFileName?: string,
    onProgress?: DownloadProgressCallback,
    photosWithStatus?: Array<{ url: string; isDownloaded?: boolean }> // 照片对象数组，包含下载状态
  ) => Promise<number> // 返回实际扣减次数的数量（未下载照片的成功下载数量）
}

/**
 * 从 URL 中提取文件名
 * @param url - 图片URL
 * @returns 文件名，如果提取失败则返回默认文件名
 */
const extractFileNameFromUrl = (
  url: string,
  defaultName: string = 'watermark_removed.jpg'
): string => {
  if (!url) {
    return defaultName
  }

  try {
    const urlPath = url.split('?')[0] // 移除查询参数
    const urlFileName = urlPath.split('/').pop() || '' // 获取路径的最后一部分
    if (urlFileName && urlFileName.includes('.')) {
      // 确保文件名包含扩展名
      return urlFileName
    }
  } catch (error) {
    console.error('提取文件名失败:', error)
  }

  return defaultName
}

/**
 * 修改下载 URL，将查询参数替换为指定尺寸
 * @param url - 原始图片URL
 * @param width - 目标宽度，默认为 1000
 * @returns 修改后的 URL
 */
const modifyDownloadUrl = (url: string, width: number = 1000): string => {
  if (!url) {
    return url
  }

  try {
    // 提取基础 URL（问号之前的部分）
    const baseUrl = url.split('?')[0]
    // 替换查询参数为指定尺寸
    return baseUrl
  } catch (error) {
    console.error('修改下载 URL 失败:', error)
    return url
  }
}

/**
 * 图片下载处理 Hook
 * 负责处理图片下载的完整流程，包括下载图片和记录去水印次数
 */
export const useImageDownload = (): UseImageDownloadReturn => {
  const dispatch = useAppDispatch()
  const groupID = useGroupID()

  /**
   * 下载单张图片
   * @param url - 图片URL
   * @param fileName - 下载的文件名（可选，如果不提供则从 originalUrl 提取）
   * @param originalUrl - 原始图片URL（可选，用于提取文件名）
   * @param onProgress - 进度回调函数（可选）
   */
  const downloadImage = useCallback(
    async (
      url: string,
      fileName?: string,
      originalUrl?: string,
      onProgress?: DownloadProgressCallback
    ): Promise<void> => {
      try {
        // 确定要使用的文件名：优先使用传入的文件名，否则从原始URL提取，最后使用默认值
        const finalFileName =
          fileName ||
          (originalUrl
            ? extractFileNameFromUrl(originalUrl, 'watermark_removed.jpg')
            : extractFileNameFromUrl(url, 'watermark_removed.jpg'))

        // 修改下载 URL，将查询参数替换为 w_1000 以获取更高分辨率
        const downloadUrl = modifyDownloadUrl(url, 1000)

        // 使用 fetch 获取图片数据
        const response = await fetch(downloadUrl)
        if (!response.ok) {
          throw new Error(`下载失败: ${response.statusText}`)
        }

        const blob = await response.blob()

        // 创建 blob URL
        const blobUrl = window.URL.createObjectURL(blob)

        // 创建下载链接
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = finalFileName
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // 清理 blob URL
        window.URL.revokeObjectURL(blobUrl)

        // 触发进度回调
        if (onProgress) {
          onProgress(1, 1)
        }

        // 记录去水印次数
        if (groupID) {
          try {
            await dispatch(
              removeWatermarkDownload({
                groupID: groupID,
                removeType: 1, // 1 表示 ai去水印
                watermarkCount: 1 // 下载图片的数量
              })
            )
          } catch (error) {
            console.error('记录去水印次数失败:', error)
            // 不阻塞用户，静默失败
          }
        }
      } catch (error) {
        console.error('下载图片失败:', error)
        throw error
      }
    },
    [dispatch, groupID]
  )

  /**
   * 批量下载图片
   * @param urls - 图片URL数组（去水印后的URL）
   * @param originalUrls - 原始图片URL数组（可选，用于提取文件名）
   * @param baseFileName - 基础文件名，默认为 'watermark_removed'（当无法从原始URL提取文件名时使用）
   * @param onProgress - 进度回调函数（可选）
   * @param photosWithStatus - 照片对象数组，包含下载状态（可选）
   * @returns 返回实际扣减次数的数量（未下载照片的成功下载数量）
   */
  const downloadImages = useCallback(
    async (
      urls: string[],
      originalUrls?: string[],
      baseFileName: string = 'watermark_removed',
      onProgress?: DownloadProgressCallback,
      photosWithStatus?: Array<{ url: string; isDownloaded?: boolean }>
    ): Promise<number> => {
      if (urls.length === 0) {
        return 0
      }

      let successCount = 0
      let notDownloadedSuccessCount = 0 // 未下载照片的成功下载数量
      const errors: string[] = []

      // 创建照片状态映射，用于快速查找照片的下载状态
      const photoStatusMap = new Map<string, boolean>()
      if (photosWithStatus) {
        photosWithStatus.forEach((photo) => {
          photoStatusMap.set(photo.url, photo.isDownloaded === true)
        })
      }

      // 逐张下载图片
      for (let index = 0; index < urls.length; index++) {
        const url = urls[index]
        const originalUrl =
          originalUrls && originalUrls[index] ? originalUrls[index] : undefined

        // 检查照片是否已下载
        // 优先使用 photosWithStatus 中的状态，如果没有提供则默认为未下载
        const isDownloaded = photoStatusMap.has(url)
          ? photoStatusMap.get(url)!
          : false

        // 从原始URL提取文件名，如果没有原始URL则使用基础文件名生成
        const fileName = originalUrl
          ? extractFileNameFromUrl(
              originalUrl,
              `${baseFileName}_${index + 1}.jpg`
            )
          : `${baseFileName}_${index + 1}.jpg`

        // 修改下载 URL，将查询参数替换为 w_1000 以获取更高分辨率
        const downloadUrl = modifyDownloadUrl(url, 1000)

        console.log('下载链接：', downloadUrl, '文件名：', fileName)

        try {
          // 使用 fetch 获取图片数据
          const response = await fetch(downloadUrl)
          if (!response.ok) {
            throw new Error(`下载失败: ${response.statusText}`)
          }

          const blob = await response.blob()

          // 创建 blob URL
          const blobUrl = window.URL.createObjectURL(blob)

          // 创建下载链接
          const link = document.createElement('a')
          link.href = blobUrl
          link.download = fileName
          link.style.display = 'none'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          // 清理 blob URL
          window.URL.revokeObjectURL(blobUrl)

          successCount++
          // 如果照片未下载过，则计入需要扣减次数的数量
          if (!isDownloaded) {
            notDownloadedSuccessCount++
          }

          // 触发进度回调
          if (onProgress) {
            onProgress(successCount, urls.length)
          }
        } catch (error) {
          const errorMsg = `下载第 ${index + 1} 张照片失败`
          message.error(errorMsg)
          console.error(errorMsg, error)
          errors.push(errorMsg)
        }
      }

      // 记录去水印次数（只记录未下载照片的成功下载数量）
      if (groupID && notDownloadedSuccessCount > 0) {
        try {
          await dispatch(
            removeWatermarkDownload({
              groupID: groupID,
              removeType: 1, // 1 表示 ai去水印
              watermarkCount: notDownloadedSuccessCount // 只扣减未下载照片的数量
            })
          )
        } catch (error) {
          console.error('记录去水印次数失败:', error)
          // 不阻塞用户，静默失败
        }
      }

      // 如果有部分失败，显示警告
      if (errors.length > 0 && errors.length < urls.length) {
        message.warning(`部分照片下载失败，成功 ${successCount} 张`)
      } else if (errors.length === urls.length) {
        throw new Error('所有照片下载失败')
      }

      return notDownloadedSuccessCount
    },
    [dispatch, groupID]
  )

  return {
    downloadImage,
    downloadImages
  }
}

export default useImageDownload
